import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../lib/i18n";

export default function OwnerLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { t, lang, setLang } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.user.role !== "owner") {
        toast.error(t("login.notowner"));
        return;
      }
      localStorage.setItem("bh_owner_token", data.token);
      navigate("/owner");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-teak border border-teak-line text-cream text-sm outline-none focus:border-gold";

  return (
    <div className="min-h-screen bg-teak acheik-bg-dark flex items-center justify-center px-4" data-testid="owner-login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4">
          <div className="flex items-center rounded-full border border-teak-line bg-teak-surface p-0.5" data-testid="lang-toggle">
            <button
              type="button"
              onClick={() => setLang("en")}
              data-testid="lang-toggle-en"
              className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors duration-200 ${lang === "en" ? "bg-gold text-ink" : "text-cream/50 hover:text-cream"}`}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLang("my")}
              data-testid="lang-toggle-my"
              style={{ fontFamily: "'Noto Sans Myanmar', sans-serif" }}
              className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors duration-200 ${lang === "my" ? "bg-gold text-ink" : "text-cream/50 hover:text-cream"}`}
            >
              မြန်မာ
            </button>
          </div>
        </div>
        <form onSubmit={submit} className="bg-teak-surface border border-teak-line rounded-2xl p-8 sm:p-10 shadow-2xl" data-testid="owner-auth-form">
          <a href="/" className="flex items-center gap-3 mb-8 hover:opacity-90 transition-opacity" data-testid="owner-login-brand-link">
            <img src="/bh-icon-180.png" alt="BurmeseHub logo" className="w-10 h-10 rounded-full" />
            <div>
              <p className="font-display font-extrabold text-cream text-lg leading-none">BURMESE <span className="text-gold">HUB</span></p>
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-cream/50 mt-1">{t("login.owner")}</p>
            </div>
          </a>
          <label className="block mb-4">
            <span className="block text-xs font-semibold uppercase tracking-wider text-cream/60 mb-2">{t("login.email")}</span>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@business.com" data-testid="owner-email-input" />
          </label>
          <label className="block mb-7">
            <span className="block text-xs font-semibold uppercase tracking-wider text-cream/60 mb-2">{t("login.password")}</span>
            <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder={t("login.pwph")} data-testid="owner-password-input" />
          </label>
          <button type="submit" disabled={busy} className="w-full bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-bold py-3.5 rounded-xl transition-colors" data-testid="owner-auth-submit">
            {busy ? t("login.signingin") : t("login.signin")}
          </button>
          <p className="mt-6 text-center text-xs text-cream/40 leading-relaxed">
            {t("login.needacct")}
            <a href="mailto:admin@burmesehub.com" className="text-gold ml-1" data-testid="owner-invite-contact">admin@burmesehub.com</a>
          </p>
        </form>
      </div>
    </div>
  );
}
